import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./DocumentVerification.css";
import { AlertPopup } from "../../Asset/common";
import { HttpMethod } from "../../Asset/httpMethod.js";
import logoImg from "../../Asset/Image/Login/Logo.png";
import {
  FiUploadCloud,
  FiCheckCircle,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiRefreshCw,
  FiFileText,
  FiUser,
  FiBriefcase,
} from "react-icons/fi";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const DOCUMENT_GROUPS = [
  {
    key: "Kyc",
    label: "KYC Documents",
    helper: "Verify your identity with any one or more of the following.",
    icon: FiUser,
    types: [
      "AadharCardFront",
      "AadharCardBack",
      "PANCard",
      "Passport",
      "VoterID",
      "DrivingLicense",
      "BankStatement",
    ],
  },
  {
    key: "Business",
    label: "Business Proof Documents",
    helper: "Verify your business with any one or more of the following.",
    icon: FiBriefcase,
    types: [
      "GSTCertificate",
      "BusinessPAN",
      "ShopActLicense",
      "CancelledCheque",
      "MSMECertificate",
    ],
  },
];

const makeKey = (category, type) => `${category}__${type}`;


// "Kyc__AadharCard" -> "Aadhar Card" (built once from DOCUMENT_GROUPS, no hardcoding)
const buildTypeLookup = () => {
  const map = {};
  DOCUMENT_GROUPS.forEach((group) => {
    group.types.forEach((type) => {
      const normalized = type.replace(/\s+/g, "");
      map[`${group.key}__${normalized}`] = type;
    });
  });
  return map;

};

function DocumentVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const UserName = location?.state?.UserName;

  // docs[key] = { file, category, documentType, fileName, contentType, previewUrl, documentId, status, expanded }
  // status: "empty" (no entry) | "selected" | "uploading" | "uploaded" | "error"
  const [docs, setDocs] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const inputRef = useRef(null);
  const pendingUploadRef = useRef(null);

  // Revoke local preview URLs when the component unmounts, to avoid leaking memory.
  useEffect(() => {
    return () => {
      Object.values(docs).forEach((doc) => {
        if (doc?.previewUrl) URL.revokeObjectURL(doc.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------
  // Fetch already-uploaded documents on page load
  // -----------------------------------------
  useEffect(() => {
    const fetchExistingDocuments = async () => {
      try {


        const res = await HttpMethod.get(
          `/api/Upload/GetUserDocumentList/GetUserDocumentList`
        );

        const list = Array.isArray(res?.data) ? res.data : res?.data?.Documents || [];

        if (Array.isArray(list) && list.length > 0) {
          const typeLookup = buildTypeLookup();

          setDocs((prev) => {
            const obj = { ...prev };

            list.forEach((item) => {
              if (!item?.IsActive) return;

              const groupKey = item.Category === "KYC" ? "Kyc" : "Business";
              const type = typeLookup[`${groupKey}__${item.DocumentType}`];
              if (!type) return; // unknown DocumentType, skip

              const key = makeKey(groupKey, type);

              obj[key] = {
                file: null, // already uploaded, no local File object
                category: groupKey,
                documentType: type,
                fileName: item.OriginalFileName,
                contentType: item.ContentType,
                previewUrl: null,
                documentId: item.DocumentId,
                status: "uploaded",
                expanded: false,
              };
            });

            return obj;
          });
        }
      } catch (err) {
        console.error("Failed to fetch existing documents", err);
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchExistingDocuments();
  }, []);

  const totalRequired = DOCUMENT_GROUPS.length; // at least 1 selected per group

  // counts a group as complete if a new file is selected OR it's already uploaded (has documentId)
  const completedGroups = DOCUMENT_GROUPS.filter((group) =>
    group.types.some((type) => {
      const doc = docs[makeKey(group.key, type)];
      return !!(doc?.file || doc?.documentId);
    })
  ).length;

  const progressPercent = Math.round((completedGroups / totalRequired) * 100);
  const canSubmit = completedGroups === totalRequired && !submitting;

  const openPicker = (category, type) => {
    pendingUploadRef.current = { category, type };
    inputRef.current?.click();
  };

  const [loginUser, setLoginUser] = useState(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("loginUserDetail"); // તમારું actual key name ચેક કરી લેજો
      if (stored) {
        setLoginUser(JSON.parse(stored));
      }
    } catch (err) {
      console.error("Failed to parse LoginUserDetails from localStorage", err);
    }
  }, []);

  const isActivationPending = loginUser && loginUser.IsActive === false;

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const pending = pendingUploadRef.current;

    if (file && pending) {
      const key = makeKey(pending.category, pending.type);

      setDocs((prev) => {
        if (prev[key]?.previewUrl) URL.revokeObjectURL(prev[key].previewUrl);
        return {
          ...prev,
          [key]: {
            file,
            category: pending.category,
            documentType: pending.type,
            fileName: file.name,
            contentType: file.type,
            previewUrl: URL.createObjectURL(file),
            documentId: null,
            status: "selected",
            expanded: prev[key]?.expanded || false,
          },
        };
      });
    }

    pendingUploadRef.current = null;
    event.target.value = "";
  };

  const toggleExpand = (key) => {
    setDocs((prev) => ({
      ...prev,
      [key]: { ...prev[key], expanded: !prev[key]?.expanded },
    }));
  };

  const downloadFile = (documentId) => {
    if (!documentId) return;
    window.open(
      `${API_URL}/api/Upload/UserDocumentDownload/UserDocumentDownload/${documentId}`,
      "_blank"
    );
  };

  // Builds Documents[0], Documents[1], Documents[2]... dynamically from
  // whichever documents were actually selected, and uploads them in one call.
  const handleSubmit = async () => {
    if (!canSubmit) {
      AlertPopup.warningPopup(
        "Please select at least one KYC document and one Business document."
      );
      return;
    }

    const entries = Object.entries(docs).filter(
      ([, doc]) => doc?.file instanceof File
    );

    if (entries.length === 0) {
      AlertPopup.warningPopup("Please select document(s).");
      return;
    }

    try {

      setSubmitting(true);

      // -----------------------------------------
      // 1. Set uploading status
      // -----------------------------------------
      setDocs(prev => {
        const obj = { ...prev };

        entries.forEach(([key]) => {
          obj[key] = {
            ...obj[key],
            status: "uploading"
          };
        });

        return obj;
      });

      // -----------------------------------------
      // 2. Create document objects
      // -----------------------------------------
      const documents = entries.map(([, doc]) => ({
        Category: doc.category === "Kyc" ? "KYC" : "Business",
        DocumentType: doc.documentType.replace(/\s+/g, ""),
        File: doc.file
      }));

      console.log("Documents Object:", documents);

      // -----------------------------------------
      // 3. Convert object into FormData
      // -----------------------------------------
      const formData = new FormData();

      documents.forEach((doc, index) => {

        formData.append(
          `Documents[${index}].Category`,
          doc.Category
        );

        formData.append(
          `Documents[${index}].DocumentType`,
          doc.DocumentType
        );

        formData.append(
          `Documents[${index}].File`,
          doc.File,
          doc.File.name
        );
      });

      // -----------------------------------------
      // 4. Debug FormData
      // -----------------------------------------
      console.log("========== FORM DATA ==========");

      for (const [key, value] of formData.entries()) {
        console.log(key, value);
      }

      // -----------------------------------------
      // 5. API Call
      // -----------------------------------------

      const response = await HttpMethod.postWithformDataDocumentUpload(
        "/api/Upload/UserDocumentUpload",
        formData
      );
      console.log("Upload Response:", response);

      // -----------------------------------------
      // 6. API Response Validation
      // -----------------------------------------
      if (!response?.Success) {
        throw new Error(
          response?.Message || response?.message || "Upload failed."
        );
      }

      // -----------------------------------------
      // 7. Get uploaded documents
      // -----------------------------------------
      const uploadedDocs = response?.Documents || [];

      // -----------------------------------------
      // 8. Update document status
      // -----------------------------------------
      setDocs(prev => {
        const obj = { ...prev };

        entries.forEach(([key], index) => {

          const apiDoc = uploadedDocs[index];

          obj[key] = {
            ...obj[key],

            status: "uploaded",

            documentId: apiDoc?.Id || apiDoc?.DocumentId,

            fileName:
              apiDoc?.OriginalFileName ||
              obj[key]?.file?.name,

            contentType:
              apiDoc?.ContentType ||
              obj[key]?.file?.type,

            expanded: false
          };
        });

        return obj;
      });

      AlertPopup.errorPopup(
        "Documents uploaded successfully."
      );

      navigate("/login");

    } catch (err) {

      console.error("Document Upload Error:", err);

      // -----------------------------------------
      // Error status
      // -----------------------------------------
      setDocs(prev => {
        const obj = { ...prev };

        entries.forEach(([key]) => {
          obj[key] = {
            ...obj[key],
            status: "error"
          };
        });

        return obj;
      });

      AlertPopup.errorPopup(
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.message ||
        "Upload failed."
      );

    } finally {

      setSubmitting(false);

    }
  };

  const facetStyle = useMemo(
    () => ({
      background: `conic-gradient(var(--dv-accent) ${progressPercent}%, var(--dv-facet-track) 0)`,
    }),
    [progressPercent]
  );

  return (
    <div className="docverify_page">
      <input
        type="file"
        ref={inputRef}
        accept="image/*,application/pdf"
        onChange={handleFileChange}
        hidden
      />

      <header className="docverify_header">
        <div className="docverify_brand">
          <img src={logoImg} alt="Sun Diam" className="docverify_logo" />
        </div>

        {isActivationPending && (
          <div className="docverify_activation_banner">
            Your account activation is pending administrator approval. Please wait, or contact the administrator for assistance.
          </div>
        )}

        <ol className="docverify_steps">
          <li className="docverify_step docverify_step-done">
            <span className="docverify_step_dot">
              <FiCheckCircle />
            </span>
            Account created
          </li>
          <li className="docverify_step docverify_step-active">
            <span className="docverify_step_dot">2</span>
            Document verification
          </li>
          <li className="docverify_step">
            <span className="docverify_step_dot">3</span>
            Sign in
          </li>
        </ol>
      </header>

      <main className="docverify_main">
        <section className="docverify_intro">
          <div className="docverify_facet" style={facetStyle}>
            <div className="docverify_facet_inner">
              <span className="docverify_facet_value">{progressPercent}%</span>
              <span className="docverify_facet_label">Selected</span>
            </div>
          </div>

          <div className="docverify_intro_copy">
            <span className="docverify_eyebrow">Almost there{UserName ? `, ${UserName}` : ""}</span>
            <h1 style={{ color: '#DC3545' }}>Mandatory Documents:</h1>
            <p className="d-none">
              Select at least one KYC document and one business proof document
              below, then submit them together. Nothing is uploaded until you
              tap &ldquo;Submit for verification&rdquo;.
            </p>

            <p className="mandatory-documents" style={{ fontWeight: 'bold', color: '#DC3545', fontSize: '15px', lineHeight: '1.5' }}>
              <strong>KYC:</strong> <em>Aadhar Card Front/Back</em>, <em>PAN Card</em>.
              <div></div>
              <strong>Business:</strong> <em>GST Certificate</em>, <em>Business PAN</em>, <em>Cancelled Cheque</em>, <em>MSME Certificate</em>.
              <div></div>
              Please ensure all required documents are uploaded for verification.
            </p>
          </div>
        </section>

        {loadingExisting && (
          <p style={{ marginBottom: 20, color: "var(--dv-text-soft)" }}>
            Loading your documents…
          </p>
        )}

        {DOCUMENT_GROUPS.map((group) => (
          <section
            className={`docverify_group docverify_group-${group.key}`}
            key={group.key}
          >
            <div className="docverify_group_head">
              <span className="docverify_group_icon">
                <group.icon />
              </span>
              <div>
                <h2>{group.label}</h2>
                <p>{group.helper}</p>
              </div>
            </div>

            <div className="docverify_grid">
              {group.types.map((type) => {
                const key = makeKey(group.key, type);
                const doc = docs[key];
                const status = doc?.status || "empty";
                const isImage = (doc?.contentType || "").startsWith("image/");
                const isPdf = (doc?.contentType || "").includes("pdf");
                const previewSrc =
                  doc?.previewUrl ||
                  (doc?.documentId
                    ? `${API_URL}/api/Upload/UserDocumentPreview/UserDocumentPreview/${doc.documentId}`
                    : null);

                return (
                  <article
                    className={`docverify_card docverify_card-${status}`}
                    key={key}
                  >
                    <div className="docverify_card_top">
                      <div className="docverify_card_icon">
                        <FiFileText />
                      </div>
                      <div className="docverify_card_title">
                        <h3>{type}</h3>
                        {doc?.fileName && (
                          <span className="docverify_filename">{doc.fileName}</span>
                        )}
                      </div>
                      <span className={`docverify_badge docverify_badge-${status}`}>
                        {status === "uploaded" && "Uploaded"}
                        {status === "uploading" && "Submitting…"}
                        {status === "selected" && "Selected"}
                        {status === "error" && "Failed"}
                        {status === "empty" && "Not selected"}
                      </span>
                    </div>

                    {doc?.file || doc?.documentId ? (
                      <div className="docverify_card_actions">
                        <button
                          type="button"
                          className="docverify_btn docverify_btn-ghost"
                          onClick={() => toggleExpand(key)}
                          disabled={status === "uploading"}
                        >
                          {doc.expanded ? <FiEyeOff /> : <FiEye />}
                          {doc.expanded ? "Hide preview" : "Preview"}
                        </button>
                        {doc.documentId && (
                          <button
                            type="button"
                            className="docverify_btn docverify_btn-ghost"
                            onClick={() => downloadFile(doc.documentId)}
                          >
                            <FiDownload />
                            Download
                          </button>
                        )}
                        <button
                          type="button"
                          className="docverify_btn docverify_btn-ghost"
                          onClick={() => openPicker(group.key, type)}
                          disabled={status === "uploading"}
                        >
                          <FiRefreshCw />
                          Replace
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="docverify_dropzone"
                        onClick={() => openPicker(group.key, type)}
                      >
                        <FiUploadCloud />
                        <span>Click to upload</span>
                      </button>
                    )}

                    {doc?.expanded && previewSrc && (
                      <div className="docverify_preview">
                        {isPdf ? (
                          <iframe
                            src={previewSrc}
                            title={type}
                            className="docverify_preview_pdf"
                          />
                        ) : isImage ? (
                          <img
                            src={previewSrc}
                            alt={type}
                            className="docverify_preview_img"
                          />
                        ) : (
                          <div className="docverify_preview_fallback">
                            No inline preview available for this file type.
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      <footer className="docverify_footer">
        <div className="docverify_footer_status">
          <div className="docverify_footer_bar">
            <div
              className="docverify_footer_bar_fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span>
            {completedGroups} of {totalRequired} document groups selected
          </span>
        </div>
        <button
          type="button"
          className="docverify_submit"
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          {submitting ? "Submitting…" : "Submit for verification"}
        </button>
      </footer>
    </div>
  );
}

export default DocumentVerification;